import { groupBy } from 'lodash';
import { POI, PoiOption } from 'components/common/types/Planner.types';
import { IncludedExcludedCounter } from 'components/common/types/IncludedExcludedCounter.types';
import { splitAndTrimCategories } from 'store/userOptions/transformPoiOptions';

export type SelectionState = { include: boolean; radius: number } | null;

export const hasChildren = (item: PoiOption): boolean => !!(item.children && item.children.length > 0);

export const getItemPath = (item: PoiOption): string => item.categoryPath || item.categoryType || '';

export const getLeafSelectionKey = (item: PoiOption): string => `${getItemPath(item)}|${item.name}`;

export const getPoiSelectionKey = (poi: POI): string => `${poi.categoryPath}|${poi.name}`;

export const getParentPath = (poi: POI): string => {
  const segments = splitAndTrimCategories(poi.categoryPath);

  return segments[segments.length - 1] === poi.name ? segments.slice(0, -1).join('/') : poi.categoryPath;
};

export const isSameOption = (a: PoiOption, b: PoiOption): boolean =>
  a.name === b.name && a.categoryType === b.categoryType && a.categoryPath === b.categoryPath;

export const matchesParent = (poi: POI, parent: POI): boolean =>
  poi.categoryPath === parent.categoryPath && poi.name === parent.name && poi.hasChildren === parent.hasChildren;

const isSameItem = (poi: POI, itemName: string, itemPath: string, itemHasChildren: boolean): boolean =>
  poi.name === itemName && poi.categoryPath === itemPath && poi.hasChildren === itemHasChildren;

const isPathAncestor = (parentPath: string, childPath: string): boolean => childPath.startsWith(`${parentPath}/`);

const areSettingsEqual = (a: SelectionState, b: SelectionState): boolean =>
  a?.include === b?.include && a?.radius === b?.radius;

const haveUniformPOISettings = (items: POI[]): boolean => {
  if (items.length === 0) return false;
  const first = items[0];

  return items.every((item) => item.include === first.include && item.radius === first.radius);
};

export const removeParent = (list: POI[], parent: POI): POI[] =>
  list.filter((poi) => !(poi.categoryPath === parent.categoryPath && poi.name === parent.name));

export const isDescendantOfItem = (poi: POI, itemName: string, itemPath: string, itemHasChildren: boolean): boolean => {
  if (isSameItem(poi, itemName, itemPath, itemHasChildren)) return false;

  return isPathAncestor(itemPath, poi.categoryPath);
};

export const isChildOfPath = (poi: POI, parentPath: string): boolean => isPathAncestor(parentPath, poi.categoryPath);

export const isLeafChildOfParent = (parent: POI, other: POI): boolean => {
  if (other === parent || other.hasChildren || !parent.hasChildren) return false;

  return isDescendantOfItem(other, parent.name, parent.categoryPath, parent.hasChildren);
};

export const getAllLeafDescendants = (option: PoiOption): PoiOption[] => {
  if (!hasChildren(option)) return [option];

  return (option.children ?? []).flatMap(getAllLeafDescendants);
};

export const findOptionByPath = (options: PoiOption[], targetPath: string): PoiOption | undefined =>
  options.reduce<PoiOption | undefined>((found, option) => {
    if (found) return found;

    const optionPath = getItemPath(option);

    if (optionPath === targetPath) return option;

    if (hasChildren(option) && targetPath.startsWith(`${optionPath}/`)) {
      return findOptionByPath(option.children ?? [], targetPath);
    }

    return undefined;
  }, undefined);

export const getLeafDescendantsUnderAncestor = (poiOptions: PoiOption[], ancestorPath: string): PoiOption[] => {
  const ancestor = findOptionByPath(poiOptions, ancestorPath);

  if (!ancestor) return [];

  return getAllLeafDescendants(ancestor);
};

export const getSiblingOptions = (poiOptions: PoiOption[], item: PoiOption): PoiOption[] => {
  const parentPath = splitAndTrimCategories(getItemPath(item)).slice(0, -1).join('/');

  if (!parentPath) return poiOptions;

  return findOptionByPath(poiOptions, parentPath)?.children ?? [];
};

export const createPOIFromOption = (option: PoiOption, include: boolean, radius: number, dataProvider: string): POI => {
  const categorySegments = splitAndTrimCategories(option.categoryType ?? '');
  const endsWithName = categorySegments[categorySegments.length - 1] === option.name;
  const parentIndex = endsWithName ? categorySegments.length - 2 : categorySegments.length - 1;
  const optionHasChildren = hasChildren(option);

  return {
    code: option.categoryType ?? '',
    name: option.name,
    include,
    radius,
    dataProvider,
    hasChildren: optionHasChildren,
    rootCategoryType: categorySegments[0],
    parentCategory: categorySegments.length > 1 ? categorySegments[parentIndex] : '',
    categoryPath: getItemPath(option),
    tooltip: `${categorySegments.join(' > ')}${optionHasChildren ? '' : ` > ${option.name}`}`,
  };
};

export const createMissingLeafPOIs = (
  leaves: PoiOption[],
  existingKeys: Set<string>,
  include: boolean,
  radius: number,
  provider: string,
): POI[] =>
  leaves.reduce<POI[]>((acc, leaf) => {
    if (!existingKeys.has(getLeafSelectionKey(leaf))) {
      acc.push(createPOIFromOption(leaf, include, radius, provider));
    }

    return acc;
  }, []);

const createParentPOI = (sibling: POI, parentName: string, parentPath: string): POI => {
  const segments = splitAndTrimCategories(parentPath);

  return {
    code: parentPath,
    name: parentName,
    include: sibling.include,
    radius: sibling.radius,
    dataProvider: sibling.dataProvider,
    hasChildren: true,
    rootCategoryType: sibling.rootCategoryType,
    parentCategory: segments.slice(0, -1).pop() || '',
    categoryPath: parentPath,
    tooltip: segments.join(' > '),
  };
};

const findSelectedPOI = (item: PoiOption, selectedPOIs: POI[]): POI | undefined => {
  const itemHasChildren = hasChildren(item);

  return selectedPOIs.find(
    (poi) => poi.code === item.categoryType && poi.name === item.name && poi.hasChildren === itemHasChildren,
  );
};

export const findSelectedAncestor = (item: PoiOption, selectedPOIs: POI[]): POI | undefined => {
  const itemPath = getItemPath(item);

  if (!item.parentCode) return undefined;

  return selectedPOIs.reduce<{ closestParent: POI | undefined; longestPathLength: number }>(
    (acc, poi) => {
      if (!poi.hasChildren) return acc;

      const poiPath = poi.categoryPath || '';
      const isAncestor = itemPath.startsWith(`${poiPath}/`);
      const isCloser = poiPath.length > acc.longestPathLength;

      if (isAncestor && isCloser) {
        return { closestParent: poi, longestPathLength: poiPath.length };
      }

      return acc;
    },
    { closestParent: undefined, longestPathLength: 0 },
  ).closestParent;
};

export const getDescendantsOfItem = (item: PoiOption, selectedPOIs: POI[]): POI[] => {
  const itemPath = getItemPath(item);

  if (!itemPath) return [];

  return selectedPOIs.filter((poi) => isDescendantOfItem(poi, item.name, itemPath, hasChildren(item)));
};

const hasOverridingDescendant = (child: PoiOption, ancestor: POI, selectedPOIs: POI[]): boolean => {
  const childPath = getItemPath(child);

  return selectedPOIs.some(
    (poi) => poi !== ancestor && isDescendantOfItem(poi, child.name, childPath, hasChildren(child)),
  );
};

const getUniformStateFromPOIs = (items: POI[]): SelectionState => {
  if (items.length === 0 || !haveUniformPOISettings(items)) return null;

  return { include: items[0].include, radius: items[0].radius };
};

export const getUniformDescendantState = (item: PoiOption, selectedPOIs: POI[]): SelectionState => {
  const directMatch = findSelectedPOI(item, selectedPOIs);

  if (directMatch) {
    return { include: directMatch.include, radius: directMatch.radius };
  }

  if (hasChildren(item)) {
    const childStates = (item.children ?? []).map((child) => getUniformDescendantState(child, selectedPOIs));
    const [firstChildState] = childStates;

    if (childStates.some((state) => state === null)) return null;

    return childStates.every((state) => areSettingsEqual(state, firstChildState)) ? firstChildState : null;
  }

  return getUniformStateFromPOIs(getDescendantsOfItem(item, selectedPOIs));
};

export const getChildrenUniformState = (
  children: PoiOption[],
  selectedPOIs: POI[],
): { isSameState: boolean; isSameIncludedState: boolean; firstChild: SelectionState } => {
  if (children.length === 0) return { isSameState: false, isSameIncludedState: false, firstChild: null };

  const childStates = children.map((child) => getUniformDescendantState(child, selectedPOIs));

  if (childStates.some((state) => state === null))
    return { isSameState: false, isSameIncludedState: false, firstChild: null };

  const first = childStates[0];

  const isSameIncludedState = childStates.every((state) => state?.include === first?.include);
  const isSameState = childStates.every((state) => areSettingsEqual(state, first));

  if (!isSameState && isSameIncludedState) return { isSameState: false, isSameIncludedState: true, firstChild: first };

  return { isSameState, isSameIncludedState, firstChild: first };
};

export const countDescendants = (descendants: POI[]): IncludedExcludedCounter =>
  descendants.reduce(
    (acc, curr) => ({
      included: curr.include ? acc.included + 1 : acc.included,
      excluded: curr.include ? acc.excluded : acc.excluded + 1,
    }),
    { included: 0, excluded: 0 },
  );

const incrementCount = (counts: IncludedExcludedCounter, include: boolean, amount = 1): void => {
  if (include) counts.included += amount;
  else counts.excluded += amount;
};

const countLeafDescendants = (child: PoiOption, include: boolean, counts: IncludedExcludedCounter): void => {
  const leafCount = getAllLeafDescendants(child).length;
  incrementCount(counts, include, leafCount);
};

const countDescendantPOI = (descendant: POI, child: PoiOption, counts: IncludedExcludedCounter): void => {
  if (descendant.hasChildren) {
    const option = findOptionByPath([child], descendant.categoryPath);
    const leafCount = option ? getAllLeafDescendants(option).length : 1;
    incrementCount(counts, descendant.include, leafCount);
  } else {
    incrementCount(counts, descendant.include);
  }
};

const countChildRecursive = (child: PoiOption, selectedPOIs: POI[], counts: IncludedExcludedCounter): void => {
  const directMatch = findSelectedPOI(child, selectedPOIs);

  if (directMatch) {
    if (hasChildren(child)) countLeafDescendants(child, directMatch.include, counts);
    else incrementCount(counts, directMatch.include);

    return;
  }

  const ancestor = findSelectedAncestor(child, selectedPOIs);

  if (ancestor && !hasOverridingDescendant(child, ancestor, selectedPOIs)) {
    if (hasChildren(child)) countLeafDescendants(child, ancestor.include, counts);
    else incrementCount(counts, ancestor.include);

    return;
  }

  const descendants = getDescendantsOfItem(child, selectedPOIs);

  if (descendants.length > 0) {
    descendants.forEach((descendant) => countDescendantPOI(descendant, child, counts));

    return;
  }

  if (hasChildren(child)) {
    (child.children ?? []).forEach((grandchild) => countChildRecursive(grandchild, selectedPOIs, counts));
  }
};

export const calculateChildrenCounts = (children: PoiOption[], selectedPOIs: POI[]): IncludedExcludedCounter =>
  children.reduce<IncludedExcludedCounter>(
    (counts, child) => {
      countChildRecursive(child, selectedPOIs, counts);

      return counts;
    },
    { included: 0, excluded: 0 },
  );

const checkConsolidation = (
  siblings: POI[],
  allPois: POI[],
  poiOptions: PoiOption[],
): { canConsolidate: boolean; parentName: string; parentPath: string } => {
  const first = siblings[0];

  if (!haveUniformPOISettings(siblings) || siblings.some((sibling) => sibling.hasChildren) || !first.parentCategory) {
    return { canConsolidate: false, parentName: '', parentPath: '' };
  }
  const parentName = first.parentCategory;
  const parentPath = getParentPath(first);

  const parentOption = findOptionByPath(poiOptions, parentPath);

  if (parentOption && parentOption.children && siblings.length !== parentOption.children.length) {
    return { canConsolidate: false, parentName: '', parentPath: '' };
  }

  if (
    allPois.some((poi) => poi.name === parentName && (poi.include !== first.include || poi.radius !== first.radius))
  ) {
    return { canConsolidate: false, parentName: '', parentPath: '' };
  }

  return { canConsolidate: true, parentName, parentPath };
};

const consolidateOnce = (pois: POI[], poiOptions: PoiOption[]): { result: POI[]; didConsolidate: boolean } => {
  const grouped = groupBy(pois, (poi) => `${getParentPath(poi)}|${poi.parentCategory}`);

  return Object.values(grouped).reduce<{ result: POI[]; didConsolidate: boolean }>(
    (acc, siblings) => {
      if (siblings.length <= 1) {
        return { ...acc, result: [...acc.result, ...siblings] };
      }

      const { canConsolidate, parentName, parentPath } = checkConsolidation(siblings, pois, poiOptions);

      if (canConsolidate) {
        return {
          result: [...acc.result, createParentPOI(siblings[0], parentName, parentPath)],
          didConsolidate: true,
        };
      }

      return { ...acc, result: [...acc.result, ...siblings] };
    },
    { result: [], didConsolidate: false },
  );
};

const shouldAbsorbLeaf = (poi: POI, allPois: POI[]): boolean => {
  if (poi.hasChildren) return false;

  return allPois.some(
    (other) =>
      other !== poi &&
      other.hasChildren &&
      ((other.name === poi.name && other.include === poi.include && other.radius === poi.radius) ||
        (other.code === poi.code && other.name !== poi.name)),
  );
};

const removeParentsWithChildren = (pois: POI[]): POI[] =>
  pois.filter((poi) => !poi.hasChildren || !pois.some((other) => isLeafChildOfParent(poi, other)));

export const consolidatePOIs = (inputPois: POI[], poiOptions: PoiOption[] = []): POI[] => {
  const cleaned = removeParentsWithChildren(inputPois);
  let consolidated = consolidateOnce(cleaned, poiOptions);

  while (consolidated.didConsolidate) {
    consolidated = consolidateOnce(consolidated.result, poiOptions);
  }
  const afterLeafCleanup = consolidated.result.filter((poi) => !shouldAbsorbLeaf(poi, consolidated.result));

  return removeParentsWithChildren(afterLeafCleanup);
};
